/* eslint-disable no-negated-condition, no-extra-boolean-cast, max-statements */
import { isToday, lastDayOfMonth, lastDayOfWeek, startOfDay, startOfMonth } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { Calendar } from "react-big-calendar";
import classNames from "classnames";
import globalize from "globalize";
import localizer from "react-big-calendar/lib/localizers/globalize";
import { useMediaQuery } from "react-responsive";
import { useQuery } from "react-query";

import { calculateFilters, filteredCourses, sortEventsByDate } from "../../helpers/event";

import AgendaCourseCard from "./components/AgendaCourseCard";
import CalendarDayView from "./components/CalendarDayView";
import CalendarListView from "./components/CalendarListView";
import CalendarToolbar from "./components/CalendarToolbar";
import CalendarWeekView from "./components/CalendarWeekView";
import EventCalendarCard from "./EventCalendarCard";
import LwcErrorBoundary from "../../components/LwcErrorBoundary";

import { getCourses } from "../../services/wp.courses.query";
import { useClassFilters } from "./components/ClassFilterProvider";

const globalLocalizer = localizer(globalize);

const EventsCalendar = () => {

	const {
		setFacets,
		filters,
		showFilters,
		toggleFilters,
		view,
		setView,
		date: today,
		setDate: setToday,
		setDateView
	} = useClassFilters();

	const isTabletOrMobile = useMediaQuery({ maxWidth: 991 });

	const [queryFilters, setQueryFilters] = useState(false);
	const [range, setRange] = useState({});
	const [courses, setCourses] = useState([]);
	const [coursesByDate, setCoursesByDate] = useState({});
	const showAll = true;

	const handleView = useCallback((newView) => newView, []);

	useEffect(() => {
		if (isTabletOrMobile && ["month", "week"].indexOf(view) !== -1) {
			setView("agenda");
		}
	}, [isTabletOrMobile, view]);

	const {
		isIdle,
		isLoading,
		isError,
		data,
		error
	} = getCourses(useQuery, queryFilters, {
		enabled: !!queryFilters,
		onSuccess: ({ facets }) => {
			setFacets(facets);
		},
		retry: false,
		retryOnMount: false
	});

	useEffect(() => {
		switch (view) {
		case "month":
		default:
			setRange({
				end: lastDayOfWeek(lastDayOfMonth(today)),
				start: isToday(today) ? startOfDay(today) : startOfMonth(today)
			});
		}
	}, [view, today]);

	useEffect(() => {
		if (range?.end && range?.start) {
			setQueryFilters(calculateFilters(range.end, range.start));
		}
	}, [range]);

	useEffect(() => {
		if (data && data.courses) {
			const sortedEvents = sortEventsByDate(data.courses);
			setCoursesByDate(sortedEvents);
		}
	}, [data]);

	useEffect(() => {
		if (data && data.courses) {
			setCourses(filteredCourses(data.courses, filters));
		}
	}, [data, filters]);

	/**
	 * Handle Day Drilldown click
	 * Fires when the "+ See X more" link
	 */
	const handleDrillDown = useCallback((date) => setDateView(date, "day"), []);

	/**
	 * Set the initial facets after query completes loading.
	 */
	useEffect(() => {
		if (!isLoading && !isError && !isIdle) {
			setFacets(data.facets);
		}
	}, [isLoading, isError, isIdle]);

	if (isError) {
		return (<span>
			Error: {error.message}
		</span>);
	}

	return (
		<>
			<CalendarToolbar
				date={today}
				facets={filters.facets}
				range={range}
				setToday={setToday}
				showAll={showAll}
				showFilters={showFilters}
				toggleFilters={toggleFilters}
				view={view}
			/>
			<div className={classNames(`calendar-container mb-4 calendar-view-${view}`, { "show-all": showAll })}>
				<LwcErrorBoundary>
					<Calendar
						components={{
							agenda: {
								event: AgendaCourseCard
							},
							coursesByDate,
							event: EventCalendarCard
						}}
						date={today}
						defaultView={view}
						events={courses}
						localizer={globalLocalizer}
						max={new Date("2022 11:59 PM")}
						messages={{
							agenda: "List",
							showMore: (total) => `+ See ${total} more`
						}}
						min={new Date("2022 6:00 AM")}
						onDrillDown={handleDrillDown}
						onNavigate={handleView}
						onView={handleView}
						showAllEvents={showAll}
						toolbar={false}
						view={view}
						views={{
							agenda: CalendarListView,
							day: CalendarDayView,
							month: true,
							week: CalendarWeekView
						}}
					/>
				</LwcErrorBoundary>
			</div>
		</>
	);
};

export default EventsCalendar;
